import { Formik, FormikProps } from 'formik';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { updateWmCountry, updateWmStoreIds } from './service';
import { Button, Input, message, ModalProps, Radio, Select, Table, TableProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import { FormOutlined, PlusOutlined, RedoOutlined } from '@ant-design/icons';
import FormField from 'components/FormField';
import StickyModal from 'components/StickyModal';
import styled from '@emotion/styled';
import isEqual from 'react-fast-compare';
import {
  Store,
  StoreScopesModal,
  StoreScopesModalProps,
  StoreScopesPreview,
} from 'components/StoreScopes';
import {
  storeIds2stores,
  storeScopes2storeIds,
} from '@knowledge/knowledges/general/StoreGroup/share';
import { removeStoreScopesAll, stores2storeScopes } from 'views/BootGuide/views/AiModel/share';
import { Preview } from '@knowledge/Activity/share';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import useModal from 'hooks/useModal';
import { useERPState, useRefreshWm } from './state';

type FV = Parameters<typeof updateWmCountry>[0];

interface WmFormProps extends TableProps<ERP.Wm> {
  initId?: string;
  storeMap: Record<number, Store>;
  onUpdate?: () => any;
  children: (form: ReactNode, formProps: FormikProps<FV>, editing: boolean) => ReactNode;
}

function WmForm({ initId, storeMap, onUpdate, children, ...otherProps }: WmFormProps) {
  const { t } = useTranslate();

  const { wmList: data } = useERPState();
  const { loading, refreshWm: onRefresh } = useRefreshWm();

  const initialValues = useMemo<FV>(() => {
    const countryNameMap: Record<ERP.Country, string> = {
      CN: '中国',
      ID: '印尼',
      SG: '新加坡',
      PH: '菲律宾',
      MY: '马来',
      TH: '泰国',
      VN: '越南',
      BR: '巴西',
      US: '美国',
      UK: '英国',
      MX: '墨西哥',
      CO: '哥伦比亚',
      PL: '波兰',
      ES: '西班牙',
      CL: '智利',
      AR: '阿根廷',
    };
    const getInitCountryList = (name: string) => {
      const matched = Object.keys(countryNameMap).filter((key) =>
        name.includes(countryNameMap[key]),
      );
      return matched.length === 1 ? matched : [];
    };
    return {
      wmsList:
        data?.reduce<FV['wmsList']>((res, { id: erpId, wmsList }) => {
          return wmsList.reduce((res, { id, name, countryList }) => {
            res.push({
              id,
              countryList:
                initId === erpId && !countryList?.length ? getInitCountryList(name) : countryList,
            });
            return res;
          }, res);
        }, []) || [],
    };
  }, [data, initId]);

  const { countryLabelMap, countryOptions } = useMemo(() => {
    const countryLabelMap: Record<ERP.Country, string> = {
      CN: t('phoneCountry.cn'),
      ID: t('印尼'),
      SG: t('region.sg'),
      PH: t('region.ph'),
      MY: t('马来'),
      TH: t('region.th'),
      VN: t('region.vn'),
      BR: t('region.br'),
      US: t('region.us'),
      UK: t('region.gb'),
      MX: t('region.mx'),
      CO: t('region.co'),
      PL: t('region.pl'),
      ES: t('region.es'),
      CL: t('region.cl'),
      AR: t('region.ar'),
    };
    const countryOptions = Object.keys(countryLabelMap).map((key) => ({
      value: key,
      label: countryLabelMap[key],
    }));
    return {
      countryLabelMap,
      countryOptions,
    };
  }, [t]);

  const [activeERPId, setActiveERPId] = useState(initId);

  const [editing, setEditing] = useState(Boolean(initId));

  const [keywords, setKeywords] = useState('');

  const activeERPWm = data?.find(({ id }) => id === activeERPId);

  const showData = useMemo(() => {
    return activeERPWm?.wmsList.filter(({ name }) => {
      return name.includes(keywords.trim());
    });
  }, [activeERPWm, keywords]);

  useEffect(() => {
    if (data?.length && (!activeERPId || data.every(({ id }) => id !== activeERPId))) {
      setActiveERPId(data[0].id);
    }
  }, [data, activeERPId]);

  const { erpType, itemQueryType, reissueItemQueryType, wmsList } = activeERPWm || {};

  const { usedStoreIdMap, usedReissueStoreIdMap } = useMemo(() => {
    const usedStoreIdMap =
      wmsList?.reduce<Record<number, string[]>>((res, { id, storeIds }) => {
        return storeIds.reduce((res, storeId) => {
          if (res[storeId]) {
            res[storeId].push(id);
          } else {
            res[storeId] = [id];
          }
          return res;
        }, res);
      }, {}) || {};
    const usedReissueStoreIdMap =
      wmsList?.reduce<Record<number, string[]>>((res, { id, reissueStoreIds }) => {
        return reissueStoreIds.reduce((res, storeId) => {
          if (res[storeId]) {
            res[storeId].push(id);
          } else {
            res[storeId] = [id];
          }
          return res;
        }, res);
      }, {}) || {};
    return {
      usedStoreIdMap,
      usedReissueStoreIdMap,
    };
  }, [wmsList]);

  const storeScopesModalState = useModal<StoreScopesModalProps>();

  const storeScopesModal = (
    <StoreScopesModal
      key={activeERPWm?.id}
      allowEmpty
      showFooterTips={false}
      visible={storeScopesModalState.visible}
      onCancel={() => storeScopesModalState.hide()}
      {...storeScopesModalState.meta}
    />
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        const submitValues = {
          ...values,
          wmsList: values.wmsList.filter(({ id, countryList }) => {
            const target = initialValues.wmsList.find((_) => _.id === id);
            return !target || !isEqual(countryList, target.countryList);
          }),
        };
        return updateWmCountry(submitValues)
          .then(() => {
            message.success(t('phrase.保存成功'));
            if (!initId) {
              setEditing(false);
            }
            onRefresh();
            onUpdate?.();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(formProps) => {
        const content = (
          <Container>
            {itemQueryType === 1 && (
              <p>
                {t(
                  '会话右侧边栏-商品中，将优先展示商品在ERP仓库中是否有货，并在有货时展示各仓库具体库存。',
                )}
                <br />
                {t(
                  '1.本土或3PF店铺将查询站点国家仓库，站点国家没有仓库则查询中国仓库；2.跨境店铺将查询中国仓库。',
                )}
              </p>
            )}
            {itemQueryType === 2 && (
              <p>
                {t(
                  '会话右侧边栏-商品中，将根据各ERP仓库【支持发货店铺】，优先展示商品在店铺对应仓库中是否有货，并在有货时展示各仓库具体库存。',
                )}
              </p>
            )}
            {reissueItemQueryType === 1 && (
              <p>
                {t(
                  '售后待办中，将展示待办关联商品在ERP仓库的具体库存，可以选择有货的仓库创建补发单。',
                )}
                <br />
                {t(
                  '1.本土或3PF店铺将查询站点国家仓库，站点国家没有仓库则查询中国仓库；2.跨境店铺将查询中国仓库。',
                )}
              </p>
            )}
            {reissueItemQueryType === 2 && (
              <p>
                {t(
                  '售后待办中，将根据各ERP仓库【支持补发店铺】，展示待办关联商品在店铺对应仓库中的具体库存，可以选择有货的仓库创建补发单。',
                )}
              </p>
            )}
            <header>
              {initId ? (
                <span />
              ) : (
                <Radio.Group
                  value={activeERPId}
                  onChange={({ target: { value } }) => setActiveERPId(value)}
                >
                  {data?.map(({ erpName, id }) => {
                    return <Radio.Button value={id}>{erpName}</Radio.Button>;
                  })}
                </Radio.Group>
              )}
              <div>
                <Input.Search
                  placeholder={t('department.请输入x', { x: t('仓库名称') })}
                  onSearch={setKeywords}
                />
                {!editing && initialValues.wmsList.length > 0 && erpType === 'JUSHUITAN' && (
                  <Button type="text" onClick={() => setEditing(true)}>
                    {t('编辑仓库所在国家')}
                  </Button>
                )}
                <StatusButton type="text" icon={<RedoOutlined />} onClick={onRefresh}>
                  {t('convList.refresh')}
                </StatusButton>
              </div>
            </header>
            <Table
              rowKey="id"
              loading={loading}
              dataSource={showData}
              pagination={false}
              {...otherProps}
              columns={[
                {
                  key: 'code',
                  dataIndex: 'code',
                  title: t('仓库编号'),
                  width: 180,
                },
                {
                  key: 'name',
                  dataIndex: 'name',
                  title: t('仓库名称'),
                  width: 200,
                },
                ...(itemQueryType === 2
                  ? [
                      {
                        key: 'storeIds',
                        title: t('支持发货店铺'),
                        width: 320,
                        render: (_: any, record: ERP.Wm) => {
                          const { id, storeIds, reissueStoreIds } = record;
                          const invalidStoreIds = [
                            {
                              storeIds: Object.keys(usedStoreIdMap)
                                .filter((storeId) => {
                                  const ids = usedStoreIdMap[storeId].filter((_) => _ !== id);
                                  return ids.length >= 10;
                                })
                                .map(Number),
                              tooltips: t('erp_bind_stores_limit_tips'),
                            },
                          ];
                          const storeDataSource = storeIds2stores(
                            activeERPWm?.storeIds || [],
                            storeMap,
                          );
                          const onChange = (storeScopes: General.StoreScope[]) => {
                            return updateWmStoreIds({
                              id,
                              storeIds: storeScopes2storeIds(
                                removeStoreScopesAll(storeScopes, storeDataSource),
                              ),
                              reissueStoreIds,
                            })
                              .then(() => {
                                onRefresh();
                                message.success(t('phrase.保存成功'));
                              })
                              .catch(() => {
                                message.error(t('phrase.保存失败，请稍后再试'));
                              });
                          };
                          if (!storeIds.length) {
                            return (
                              <Button
                                style={{ paddingLeft: 0 }}
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={() =>
                                  storeScopesModalState.show({
                                    invalidStoreIds,
                                    storeDataSource,
                                    onChange,
                                  })
                                }
                              >
                                {t('选择店铺')}
                              </Button>
                            );
                          }
                          const storeScopes = stores2storeScopes(
                            storeIds2stores(storeIds, storeMap),
                          );
                          return (
                            <div>
                              {t('storeSelector.已选{x}家店铺', { x: storeIds.length })}
                              <Preview
                                content={
                                  <PreviewWrapper>
                                    <StoreScopesPreview
                                      value={storeScopes}
                                      showSearch={false}
                                      showTitle={false}
                                      storeMap={storeMap}
                                    />
                                  </PreviewWrapper>
                                }
                              />
                              <Button
                                type="link"
                                icon={<FormOutlined />}
                                onClick={() =>
                                  storeScopesModalState.show({
                                    invalidStoreIds,
                                    storeDataSource,
                                    value: storeScopes,
                                    onChange,
                                  })
                                }
                              >
                                {t('marketing.编辑')}
                              </Button>
                            </div>
                          );
                        },
                      },
                    ]
                  : []),
                ...(reissueItemQueryType === 2
                  ? [
                      {
                        key: 'reissueStoreIds',
                        title: t('支持补发店铺'),
                        width: 320,
                        render: (_: any, record: ERP.Wm) => {
                          const { id, storeIds, reissueStoreIds } = record;
                          const invalidStoreIds = [
                            {
                              storeIds: Object.keys(usedReissueStoreIdMap)
                                .filter((storeId) => {
                                  const ids = usedReissueStoreIdMap[storeId].filter(
                                    (_) => _ !== id,
                                  );
                                  return ids.length >= 10;
                                })
                                .map(Number),
                              tooltips: t('erp_bind_stores_limit_tips'),
                            },
                          ];
                          const storeDataSource = storeIds2stores(
                            activeERPWm?.storeIds || [],
                            storeMap,
                          );
                          const onChange = (storeScopes: General.StoreScope[]) => {
                            return updateWmStoreIds({
                              id,
                              storeIds,
                              reissueStoreIds: storeScopes2storeIds(
                                removeStoreScopesAll(storeScopes, storeDataSource),
                              ),
                            })
                              .then(() => {
                                onRefresh();
                                message.success(t('phrase.保存成功'));
                              })
                              .catch(() => {
                                message.error(t('phrase.保存失败，请稍后再试'));
                              });
                          };
                          if (!reissueStoreIds.length) {
                            return (
                              <Button
                                style={{ paddingLeft: 0 }}
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={() =>
                                  storeScopesModalState.show({
                                    invalidStoreIds,
                                    storeDataSource,
                                    onChange,
                                  })
                                }
                              >
                                {t('选择店铺')}
                              </Button>
                            );
                          }
                          const storeScopes = stores2storeScopes(
                            storeIds2stores(reissueStoreIds, storeMap),
                          );
                          return (
                            <div>
                              {t('storeSelector.已选{x}家店铺', { x: reissueStoreIds.length })}
                              <Preview
                                content={
                                  <PreviewWrapper>
                                    <StoreScopesPreview
                                      value={storeScopes}
                                      showSearch={false}
                                      showTitle={false}
                                      storeMap={storeMap}
                                    />
                                  </PreviewWrapper>
                                }
                              />
                              <Button
                                type="link"
                                icon={<FormOutlined />}
                                onClick={() =>
                                  storeScopesModalState.show({
                                    invalidStoreIds,
                                    storeDataSource,
                                    value: storeScopes,
                                    onChange,
                                  })
                                }
                              >
                                {t('marketing.编辑')}
                              </Button>
                            </div>
                          );
                        },
                      },
                    ]
                  : []),
                {
                  key: 'countryList',
                  dataIndex: 'countryList',
                  title: t('所在国家'),
                  width: 250,
                  render: (countryList: ERP.Country[] = [], { id }) => {
                    if (!editing || erpType !== 'JUSHUITAN') {
                      return (
                        countryList.map((country) => countryLabelMap[country]).join(' | ') || '-'
                      );
                    }
                    const index = initialValues.wmsList.findIndex((value) => value.id === id);
                    if (index > -1) {
                      return (
                        <FormField<FV['wmsList'][0]['countryList']>
                          name={`wmsList.${index}.countryList`}
                          noStyle
                        >
                          {({ field: { value, onChange, onBlur } }) => {
                            return (
                              <Select
                                allowClear
                                placeholder={t('请选择')}
                                options={countryOptions}
                                value={value?.[0]}
                                onChange={(value) => onChange(value ? [value] : [])}
                                onBlur={onBlur}
                              />
                            );
                          }}
                        </FormField>
                      );
                    }
                  },
                },
              ]}
            />
            {storeScopesModal}
          </Container>
        );

        return children(content, formProps, editing);
      }}
    </Formik>
  );
}

export function WmModal({
  initId,
  storeMap,
  onCancel,
  ...otherProps
}: ModalProps & Pick<WmFormProps, 'initId' | 'storeMap'>) {
  const { t } = useTranslate();

  return (
    <WmForm initId={initId} storeMap={storeMap} onUpdate={() => onCancel?.({} as any)}>
      {(form, { isSubmitting, handleSubmit }) => {
        return (
          <StickyModal
            title={t('ERP仓库信息管理')}
            width={1200}
            onCancel={onCancel}
            okText={t('确认仓库所在国家')}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            {...otherProps}
          >
            <div style={{ margin: '-24px' }}>{form}</div>
          </StickyModal>
        );
      }}
    </WmForm>
  );
}

const Container = styled.div`
  padding: 24px;
  background: var(--fett-bg-panel);
  > p {
    margin-bottom: 0;
    padding: 8px 12px;
    background: var(--fett-surface-warning-bg);
    & + p {
      margin-top: 14px;
    }
  }
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 16px 0;
    > span {
      width: 200px;
    }
    > div {
      display: flex;
      align-items: center;
    }
  }
  .ant-table {
    .ant-select {
      min-width: 200px;
    }
  }
`;

export default WmForm;
